// REPORT-01 — utilidad compartida de DESCARGA e IMPRESIÓN de reportes.
// El PDF lo genera el BACKEND y viaja como blob por POST autenticado: nunca hay token en la URL,
// nunca se construye una página HTML imprimible y no hay motor de impresión en el cliente.
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MENSAJE = {
  413: "El periodo solicitado es demasiado grande para un solo documento. Acota las fechas o los filtros.",
  429: "Hay una exportación en curso. Espera unos segundos y vuelve a intentarlo.",
  409: "El caso cambió mientras se generaba el reporte. Vuelve a solicitarlo.",
  403: "Tu cuenta no tiene autorización para exportar reportes.",
  404: "No se encontró el recurso solicitado para este reporte.",
  504: "La generación tardó demasiado. Acota el periodo y vuelve a intentarlo.",
};

// Un error del backend llega como Blob cuando la respuesta se pidió como blob: se lee el texto para
// poder mostrar el motivo REAL (p. ej. «El periodo máximo es de 92 días») y no un genérico.
async function detalleDeError(e) {
  const datos = e?.response?.data;
  if (datos instanceof Blob) {
    try {
      const texto = await datos.text();
      return formatApiErrorDetail(JSON.parse(texto).detail);
    } catch {
      return null;
    }
  }
  return formatApiErrorDetail(datos?.detail);
}

async function avisarError(e) {
  const status = e?.response?.status;
  const backend = await detalleDeError(e);
  toast.error(MENSAJE[status] || "No se pudo generar el reporte.", {
    description: typeof backend === "string" && backend.length > 3 ? backend : undefined,
    duration: 12000,
  });
}

async function pedirBlob(path, body) {
  const { data, headers } = await api.post(path, body || {}, { responseType: "blob" });
  return { blob: data, tipo: headers?.["content-type"] || "application/pdf" };
}

// Descarga: el blob se libera SIEMPRE (incluido el camino de error) para no dejar memoria retenida.
export async function descargarReporte(path, body, nombreArchivo) {
  let url = null;
  try {
    const { blob } = await pedirBlob(path, body);
    url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombreArchivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  } catch (e) {
    avisarError(e);
    return false;
  } finally {
    if (url) setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
}

// Impresión: MISMO PDF. En Chrome/Edge/Firefox se abre el diálogo del navegador desde un iframe
// oculto; en Safari/WebKit el iframe no expone `print()` sobre un blob, así que se abre el PDF en
// una pestaña y el usuario imprime desde el visor (fallback seguro, sin motor adicional).
export async function imprimirReporte(path, body) {
  let url = null;
  // Safari/iOS bloquea `window.open` que ocurre DESPUÉS de un await (ya no hay gesto del usuario).
  // La pestaña se reserva AQUÍ, de forma síncrona dentro del click, y se le asigna el PDF cuando
  // llega el blob. Si el navegador aun así la niega, se avisa y se ofrece la descarga.
  const esWebKit = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
  const reservada = esWebKit ? window.open("", "_blank") : null;
  try {
    const { blob } = await pedirBlob(path, body);
    url = URL.createObjectURL(blob);
    if (esWebKit) {
      const w = reservada || window.open(url, "_blank");
      if (!w) {
        toast.warning("Permite las ventanas emergentes para ver el PDF, o usa Descargar PDF.");
        URL.revokeObjectURL(url);
        url = null;
        return false;
      }
      if (reservada) w.location.href = url;
      toast.info("Se abrió el documento. Usa Imprimir o Compartir desde el visor del navegador.");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      url = null;
      return true;
    }
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = url;
    const limpiar = () => {
      if (iframe.isConnected) iframe.remove();
      URL.revokeObjectURL(url);
    };
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        window.open(url, "_blank");
      }
      // El diálogo de impresión es del navegador: no se afirma que se imprimió, sólo se libera.
      setTimeout(limpiar, 60000);
    };
    document.body.appendChild(iframe);
    url = null;
    return true;
  } catch (e) {
    if (reservada) reservada.close();
    avisarError(e);
    return false;
  } finally {
    if (url) setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
}

// Capacidad de exportar: espeja el backend (report.export para institucional; interno siempre).
// La autorización EFECTIVA sigue siendo del servidor: esto sólo evita ofrecer lo que será 403.
export function puedeExportar(user) {
  if (!user) return false;
  if (user.actor_type !== "external") return true;
  return (user.permissions || []).includes("report.export");
}
